import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";
import {
  CurrencyDollar,
  TrendUp,
  ShoppingBag,
  Sword,
  Trophy,
  CalendarCheck,
  Warning,
  ArrowClockwise,
} from "@phosphor-icons/react";

const PERIODS = [
  { key: 7, label: "7 dias" },
  { key: 30, label: "30 dias" },
  { key: 90, label: "90 dias" },
];

const SOURCE_COLORS = {
  rentals: "#CCFF00",
  tournaments: "#60A5FA",
  retas: "#F87171",
  shop: "#FBBF24",
};

const SOURCE_LABELS = {
  rentals: "Rentas de Canchas",
  tournaments: "Torneos",
  retas: "Retas",
  shop: "Tienda",
};

const formatMXN = (v) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }).format(v || 0);

function KpiCard({ label, value, accent, icon: Icon, hint, testid }) {
  return (
    <div className="card-tactical p-5 relative overflow-hidden" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase font-semibold text-gray-500">{label}</p>
          <p className={`font-['Barlow_Condensed'] text-3xl sm:text-4xl font-black mt-1 ${accent}`}>{value}</p>
          {hint && <p className="text-[11px] text-gray-500 mt-1 font-['JetBrains_Mono']">{hint}</p>}
        </div>
        <Icon size={28} weight="duotone" className={accent} />
      </div>
    </div>
  );
}

function SourceRow({ source, amount, count, total, color }) {
  const pct = total > 0 ? Math.round((amount / total) * 100) : 0;
  return (
    <div className="space-y-1.5" data-testid={`source-row-${source}`}>
      <div className="flex items-center justify-between text-xs">
        <span className="flex items-center gap-2 text-gray-300 font-semibold uppercase tracking-wider">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
          {SOURCE_LABELS[source]}
        </span>
        <span className="font-['JetBrains_Mono'] text-white">{formatMXN(amount)} <span className="text-gray-500">· {pct}%</span></span>
      </div>
      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      {count > 0 && <p className="text-[10px] text-gray-500 font-['JetBrains_Mono']">{count} transacciones</p>}
    </div>
  );
}

export default function RevenueDashboard() {
  const { user } = useAuth();
  const [period, setPeriod] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = (showSpinner = true) => {
    if (showSpinner) setLoading(true); else setRefreshing(true);
    api.get(`/admin/revenue?days=${period}`)
      .then(({ data }) => setData(data))
      .catch((err) => toast.error(err.response?.data?.detail || "Error al cargar ingresos"))
      .finally(() => { setLoading(false); setRefreshing(false); });
  };

  useEffect(() => { fetchData(true); }, [period]);

  const pieData = useMemo(() => {
    if (!data) return [];
    return Object.entries(data.breakdown || {})
      .filter(([, v]) => v > 0)
      .map(([k, v]) => ({ name: SOURCE_LABELS[k] || k, value: v, key: k }));
  }, [data]);

  if (user && user.role !== "admin" && user.role !== "directivo") {
    return (
      <div className="card-tactical p-12 text-center">
        <Warning size={48} weight="duotone" className="text-gray-600 mx-auto mb-4" />
        <p className="text-gray-400">Solo admin y directivos pueden ver el dashboard de ingresos</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const totalBreakdown = Object.values(data.breakdown).reduce((s, v) => s + v, 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="revenue-dashboard-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">
            {data.scope === "club" ? "Ingresos del Club" : "Ingresos Globales"}
          </p>
          <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black uppercase tracking-tighter">Dashboard de Ingresos</h1>
          <p className="text-xs text-gray-500 mt-1 font-['JetBrains_Mono']">
            Moneda: {data.currency} · Actualizado: {new Date(data.generated_at).toLocaleString("es-MX")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-[#0A0A0A] border border-white/10" data-testid="period-selector">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                className={`px-3 h-10 text-xs font-semibold uppercase tracking-wider transition-colors ${
                  period === p.key ? "bg-[#CCFF00] text-black" : "text-gray-400 hover:text-[#CCFF00]"
                }`}
                data-testid={`period-${p.key}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            onClick={() => fetchData(false)}
            disabled={refreshing}
            className="h-10 border-white/10 text-gray-300 hover:bg-white/5 hover:text-[#CCFF00]"
            data-testid="refresh-btn"
          >
            <ArrowClockwise size={14} className={`mr-1.5 ${refreshing ? "animate-spin" : ""}`} /> Actualizar
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard testid="kpi-today" label="Hoy" value={formatMXN(data.periods.today)} accent="text-[#CCFF00]" icon={TrendUp} />
        <KpiCard testid="kpi-week" label="Esta semana" value={formatMXN(data.periods.week)} accent="text-blue-400" icon={TrendUp} />
        <KpiCard testid="kpi-month" label="Este mes" value={formatMXN(data.periods.month)} accent="text-yellow-400" icon={TrendUp} />
        <KpiCard testid="kpi-total" label="Total historico" value={formatMXN(data.periods.total)} accent="text-white" icon={CurrencyDollar} hint="Desde el inicio" />
      </div>

      {/* Pending alerts */}
      {(data.pending.shop_orders_pending > 0 || data.pending.shop_orders_in_review > 0 || data.pending.rentals_in_review > 0) && (
        <div className="card-tactical p-4 border-yellow-500/30 bg-yellow-500/5" data-testid="pending-alerts">
          <p className="text-xs uppercase tracking-wider font-semibold text-yellow-400 mb-2 flex items-center gap-2">
            <Warning size={14} weight="fill" /> Pendientes por revisar
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            {data.pending.shop_orders_pending > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Pedidos sin comprobante</span>
                <span className="text-yellow-400 font-['JetBrains_Mono'] font-bold">{data.pending.shop_orders_pending}</span>
              </div>
            )}
            {data.pending.shop_orders_in_review > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Pedidos en revision</span>
                <span className="text-yellow-400 font-['JetBrains_Mono'] font-bold">{data.pending.shop_orders_in_review}</span>
              </div>
            )}
            {data.pending.rentals_in_review > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Rentas en revision</span>
                <span className="text-yellow-400 font-['JetBrains_Mono'] font-bold">{data.pending.rentals_in_review}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Timeline chart */}
      <div className="card-tactical p-5" data-testid="revenue-timeline">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00]">Tendencia</p>
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase mt-1">Ingresos ultimos {period} dias</h2>
          </div>
        </div>
        <div style={{ width: "100%", height: 300 }}>
          <ResponsiveContainer>
            <AreaChart data={data.timeline} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
              <defs>
                {Object.entries(SOURCE_COLORS).map(([k, c]) => (
                  <linearGradient key={k} id={`grad-${k}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={c} stopOpacity={0.55} />
                    <stop offset="100%" stopColor={c} stopOpacity={0.02} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" stroke="#6B7280" fontSize={10} tickFormatter={(d) => d.slice(5)} />
              <YAxis stroke="#6B7280" fontSize={10} tickFormatter={(v) => `$${v}`} />
              <Tooltip
                contentStyle={{ background: "#0A0A0A", border: "1px solid rgba(255,255,255,0.1)", fontSize: "12px" }}
                formatter={(v, name) => [formatMXN(v), SOURCE_LABELS[name] || name]}
              />
              <Legend wrapperStyle={{ fontSize: "11px" }} formatter={(v) => SOURCE_LABELS[v] || v} />
              <Area type="monotone" dataKey="rentals" stackId="1" stroke={SOURCE_COLORS.rentals} fill={`url(#grad-rentals)`} strokeWidth={2} />
              <Area type="monotone" dataKey="tournaments" stackId="1" stroke={SOURCE_COLORS.tournaments} fill={`url(#grad-tournaments)`} strokeWidth={2} />
              <Area type="monotone" dataKey="retas" stackId="1" stroke={SOURCE_COLORS.retas} fill={`url(#grad-retas)`} strokeWidth={2} />
              <Area type="monotone" dataKey="shop" stackId="1" stroke={SOURCE_COLORS.shop} fill={`url(#grad-shop)`} strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Breakdown + Pie */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card-tactical p-5" data-testid="breakdown-list">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00]">Desglose por fuente</p>
          <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase mt-1 mb-5">De donde proviene tu ingreso</h2>
          <div className="space-y-4">
            <SourceRow source="rentals" amount={data.breakdown.rentals} count={data.counts.rentals} total={totalBreakdown} color={SOURCE_COLORS.rentals} />
            <SourceRow source="tournaments" amount={data.breakdown.tournaments} count={data.counts.tournaments} total={totalBreakdown} color={SOURCE_COLORS.tournaments} />
            <SourceRow source="retas" amount={data.breakdown.retas} count={data.counts.retas} total={totalBreakdown} color={SOURCE_COLORS.retas} />
            <SourceRow source="shop" amount={data.breakdown.shop} count={data.counts.shop_orders} total={totalBreakdown} color={SOURCE_COLORS.shop} />
          </div>

          <div className="mt-6 pt-5 border-t border-white/5 grid grid-cols-4 gap-2">
            <div className="text-center">
              <CalendarCheck size={18} weight="duotone" className="text-[#CCFF00] mx-auto mb-1" />
              <p className="text-[10px] uppercase tracking-wider text-gray-500">Rentas</p>
              <p className="font-['JetBrains_Mono'] text-sm text-white font-bold">{data.counts.rentals}</p>
            </div>
            <div className="text-center">
              <Trophy size={18} weight="duotone" className="text-blue-400 mx-auto mb-1" />
              <p className="text-[10px] uppercase tracking-wider text-gray-500">Torneos</p>
              <p className="font-['JetBrains_Mono'] text-sm text-white font-bold">{data.counts.tournaments}</p>
            </div>
            <div className="text-center">
              <Sword size={18} weight="duotone" className="text-red-400 mx-auto mb-1" />
              <p className="text-[10px] uppercase tracking-wider text-gray-500">Retas</p>
              <p className="font-['JetBrains_Mono'] text-sm text-white font-bold">{data.counts.retas}</p>
            </div>
            <div className="text-center">
              <ShoppingBag size={18} weight="duotone" className="text-yellow-400 mx-auto mb-1" />
              <p className="text-[10px] uppercase tracking-wider text-gray-500">Pedidos</p>
              <p className="font-['JetBrains_Mono'] text-sm text-white font-bold">{data.counts.shop_orders}</p>
            </div>
          </div>
        </div>

        <div className="card-tactical p-5" data-testid="pie-chart">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00]">Distribucion</p>
          <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase mt-1 mb-3">Participacion por fuente</h2>
          {pieData.length === 0 ? (
            <div className="h-64 flex items-center justify-center">
              <div className="text-center">
                <CurrencyDollar size={48} weight="duotone" className="text-gray-700 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Sin ingresos registrados aun</p>
                <p className="text-xs text-gray-600 mt-1">Cuando apruebes pagos, apareceran aqui</p>
              </div>
            </div>
          ) : (
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} innerRadius={55} paddingAngle={2}>
                    {pieData.map((entry) => (
                      <Cell key={entry.key} fill={SOURCE_COLORS[entry.key]} stroke="#0A0A0A" strokeWidth={2} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: "#0A0A0A", border: "1px solid rgba(255,255,255,0.1)", fontSize: "12px" }}
                    formatter={(v) => formatMXN(v)}
                  />
                  <Legend wrapperStyle={{ fontSize: "11px" }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Top clubs (only shown for admin / global scope) */}
      {data.scope === "global" && data.top_clubs && data.top_clubs.length > 0 && (
        <div className="card-tactical p-5" data-testid="top-clubs">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00]">Top clubes</p>
          <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase mt-1 mb-4">Ranking por ingresos</h2>
          <div style={{ width: "100%", height: 40 + data.top_clubs.length * 44 }}>
            <ResponsiveContainer>
              <BarChart data={data.top_clubs} layout="vertical" margin={{ left: 10, right: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} />
                <XAxis type="number" stroke="#6B7280" fontSize={10} tickFormatter={(v) => `$${v}`} />
                <YAxis type="category" dataKey="name" stroke="#D1D5DB" fontSize={11} width={140} />
                <Tooltip
                  contentStyle={{ background: "#0A0A0A", border: "1px solid rgba(255,255,255,0.1)", fontSize: "12px" }}
                  formatter={(v) => [formatMXN(v), "Ingresos"]}
                />
                <Bar dataKey="revenue" fill="#CCFF00" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
